import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  MessageSquare,
  MessageCircle,
  Calendar,
  User,
  Shield,
  ScrollText,
  X,
  Users,
  MapPin,
  Server,
  Wifi,
  Gauge,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  Send,
  ThumbsUp,
  DollarSign,
  AlertTriangle,
  SlidersHorizontal,
  ShoppingBag,
  Coffee
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import textConfig from '../config/textConfig';
import kilnSmallLogo from '../images/kiln-small-logo.jpg';
import '../styles/CollapsibleSidebar.css';

// Module-level cache so navigation doesn't re-trigger loading
let _permCache = null;
let _permCacheUserId = null;

const CollapsibleSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermissions } = usePermissions();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  // If user changed, clear cache
  if (_permCacheUserId !== user?.id) {
    _permCache = null;
    _permCacheUserId = user?.id;
  }

  const [permissions, setPermissions] = useState(_permCache || {});
  const [ready, setReady] = useState(!!_permCache);

  const isElevatedRole = user?.role && user.role !== 'user' && user.role !== 'guest';
  const isSuperAdmin = user?.role === 'super_admin';

  // Decide if we need to load permissions at all
  // Super admin: no loading needed (sees everything)
  // Regular user: no loading needed (sees Chat + Profile only)
  // Other elevated: need to load permissions
  const needsPermissionLoad = isElevatedRole && !isSuperAdmin;

  // Load collapsed state from localStorage
  useEffect(() => {
    if (!isMobile) {
      const saved = localStorage.getItem('sidebarCollapsed');
      if (saved === 'true') setIsCollapsed(true);
    }
  }, [isMobile]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setIsCollapsed(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
    window.dispatchEvent(new Event('sidebarToggled'));
  };

  // Load permissions for elevated non-super-admin roles
  useEffect(() => {
    if (!needsPermissionLoad) {
      setReady(true);
      return;
    }
    // Already cached from previous render/navigation
    if (_permCache) {
      setPermissions(_permCache);
      setReady(true);
      return;
    }

    let cancelled = false;
    const load = async () => {
      // MUST stay in step with the perm on every item in allAdminItems below.
      // A permission changed in one list and not the other silently hides the
      // menu item — the nav asks for something that was never fetched.
      const results = await hasPermissions([
        { resource: 'dashboard', action: 'view' },
        { resource: 'users', action: 'read' },
        { resource: 'conversations', action: 'view_live' },
        { resource: 'locations', action: 'read' },
        { resource: 'community', action: 'read' },
        { resource: 'resources', action: 'read' },
        { resource: 'amenities', action: 'read' },
        { resource: 'faqs', action: 'read' },
        { resource: 'bulk_sms', action: 'read' },
        { resource: 'feedback', action: 'read' },
        { resource: 'sms_costs', action: 'read' },
        { resource: 'logs', action: 'read' },
        { resource: 'errors', action: 'read' },
        { resource: 'developer_tools', action: 'read' },
        { resource: 'settings', action: 'read' },
        { resource: 'roles', action: 'read' },
        { resource: 'members', action: 'scorecard' }
      ]);
      if (!cancelled) {
        _permCache = results;
        setPermissions(results);
        setReady(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [hasPermissions, needsPermissionLoad]);

  // All possible admin nav items
  const allAdminItems = useMemo(() => [
    { path: '/', label: 'Dashboard', icon: BarChart3, id: 'dashboard', perm: 'dashboard:view' },
    { path: '/admin', label: 'User Management', icon: Shield, id: 'user-management', perm: 'users:read' },
    { path: '/members/scorecard', label: 'Member Scorecard', icon: Gauge, id: 'member-scorecard', perm: 'members:scorecard' },
    { path: '/admin/live-conversations', label: 'Live Conversations', icon: MessageCircle, id: 'live-conversations', perm: 'conversations:view_live' },
    { path: '/admin/locations', label: 'Locations', icon: MapPin, id: 'locations', perm: 'locations:read' },
    { path: '/admin/community', label: 'Community Team', icon: Users, id: 'community', perm: 'community:read' },
    { path: '/admin/resources', label: 'Resources', icon: Server, id: 'resources', perm: 'resources:read' },
    { path: '/admin/amenities', label: 'Amenities', icon: Wifi, id: 'amenities', perm: 'amenities:read' },
    { path: '/workspace-faqs', label: 'FAQs', icon: HelpCircle, id: 'workspace-faqs', perm: 'faqs:read' },
    { path: '/admin/bulk-sms', label: 'Announcements', icon: Send, id: 'bulk-sms', perm: 'bulk_sms:read' },
    { path: '/admin/feedback', label: 'Message Feedback', icon: ThumbsUp, id: 'message-feedback', perm: 'feedback:read' },
    { path: '/admin/sms-costs', label: 'SMS Costs', icon: DollarSign, id: 'sms-costs', perm: 'sms_costs:read' },
    {
      id: 'logs-group',
      label: 'Logs',
      icon: ScrollText,
      // Group-level permission: the whole "Logs" group is hidden unless the user
      // has this. Grant it to anyone who should see the group in the nav.
      perm: 'logs:read',
      children: [
        { path: '/admin/logs', label: 'System Logs', icon: ScrollText, id: 'logs', perm: 'logs:read' },
        { path: '/admin/errors', label: 'Errors', icon: AlertTriangle, id: 'errors', perm: 'errors:read' },
      ],
    },
    { path: '/settings', label: 'Settings', icon: Settings, id: 'settings', perm: 'developer_tools:read' },
    { path: '/admin/settings', label: 'App Settings', icon: SlidersHorizontal, id: 'app-settings', perm: 'settings:read' },
    { path: '/admin/roles', label: 'Role Management', icon: Users, id: 'role-management', perm: 'roles:read' },
    { path: '/demo/food-order', label: 'Ordering Demo', icon: ShoppingBag, id: 'ordering-demo', superAdminOnly: true },
    { path: '/demo/cafe-order', label: 'Café Demo', icon: Coffee, id: 'cafe-demo', superAdminOnly: true },
  ], []);

  // Build final nav items
  const navItems = useMemo(() => {
    // Chat leads the nav; Profile is always pinned last, with whatever admin
    // items the user can see sitting between them.
    const chat = { path: '/chat', label: 'Chat', icon: MessageSquare, id: 'chat' };
    const profile = { path: '/profile', label: 'Profile', icon: User, id: 'profile' };
    // Every staff member may connect and manage their OWN calendar, so this needs
    // no permission. calendar:read_all only unlocks the everyone-views, which the
    // page gates itself. (Members reach it from the profile dropdown instead.)
    const calendar = { path: '/google-calendar', label: 'Google Calendar', icon: Calendar, id: 'google-calendar' };

    // Regular user: just Chat + Profile
    if (!isElevatedRole) return [chat, profile];

    // Super admin: everything, no waiting
    if (isSuperAdmin) return [chat, calendar, ...allAdminItems, profile];

    // Other elevated roles: only after permissions are ready
    if (!ready) return []; // Empty — skeletons will show instead

    const permitted = allAdminItems
      .map(item => {
        // Group item: the group needs its OWN permission to appear at all; then
        // show only the children the user can also see. Drop the group if the
        // user lacks the group perm or none of its children are visible.
        // superAdminOnly items never reach this branch's users — the super
        // admin case already returned above.
        if (item.superAdminOnly) return null;
        if (item.children) {
          if (item.perm && permissions[item.perm] !== true) return null;
          const kids = item.children.filter(child => permissions[child.perm] === true);
          return kids.length ? { ...item, children: kids } : null;
        }
        return permissions[item.perm] === true ? item : null;
      })
      .filter(Boolean);
    return [chat, calendar, ...permitted, profile];
  }, [isElevatedRole, isSuperAdmin, ready, permissions, allAdminItems]);

  // A group is expanded if the user toggled it open, or (by default) the current
  // route is one of its children.
  const isGroupExpanded = (item) => {
    if (expandedGroups[item.id] !== undefined) return expandedGroups[item.id];
    return item.children.some(child => child.path === location.pathname);
  };

  const toggleGroup = (item) => {
    const current = isGroupExpanded(item);
    setExpandedGroups(prev => ({ ...prev, [item.id]: !current }));
  };

  // Should we show skeletons?
  const showSkeletons = needsPermissionLoad && !ready;

  const handleToggleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleCollapsed();
    }
  };

  const mobileClasses = isMobile
    ? isOpen ? 'sidebar-open-mobile' : 'sidebar-hidden-mobile'
    : '';

  const desktopClasses = !isMobile
    ? isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'
    : '';

  return (
    <>
      {isMobile && isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        className={`collapsible-sidebar ${desktopClasses} ${mobileClasses}`}
        data-testid="collapsible-sidebar"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            {!isCollapsed || isMobile ? (
              <>
                <img
                  src="/Concierge-logo.jpg"
                  alt="Concierge Logo"
                  className="sidebar-logo-img"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span className="sidebar-logo-text" style={{ display: 'none' }}>
                  {textConfig.appName}
                </span>
              </>
            ) : (
              <img
                src={kilnSmallLogo}
                alt="Kiln Logo"
                className="sidebar-logo-collapsed-img"
              />
            )}
          </Link>

          {!isMobile && (
            <button
              className="sidebar-toggle"
              onClick={toggleCollapsed}
              onKeyDown={handleToggleKeyDown}
              data-testid="sidebar-toggle"
              aria-label="Toggle sidebar"
              aria-expanded={!isCollapsed}
            >
              {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
          )}

          {isMobile && (
            <button className="sidebar-close" onClick={onClose}>
              <X size={24} />
            </button>
          )}
        </div>

        <nav className="sidebar-nav">
          {showSkeletons ? (
            /* ALL skeletons — nothing real shows until permissions are ready */
            [1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={`skel-${i}`} className="nav-item-wrapper">
                <div className="sidebar-link nav-skeleton-item">
                  <span className="nav-icon nav-skeleton-icon" />
                  {(!isCollapsed || isMobile) && (
                    <span className="nav-skeleton-label" />
                  )}
                </div>
              </div>
            ))
          ) : (
            navItems.map((item) => {
              const Icon = item.icon;

              // Group item: parent toggle + nested submenu (e.g. Logs → System Logs, Errors)
              if (item.children) {
                const expanded = isGroupExpanded(item);
                const groupActive = item.children.some(child => child.path === location.pathname);

                return (
                  <div
                    key={item.id}
                    className="nav-item-wrapper nav-group"
                    onMouseEnter={() => { if (isCollapsed) setHoveredItem(item.id); }}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <button
                      type="button"
                      className={`sidebar-link nav-group-toggle ${groupActive ? 'nav-group-active' : ''}`}
                      onClick={() => toggleGroup(item)}
                      data-testid={`nav-group-${item.id}`}
                      aria-expanded={expanded}
                      title={isCollapsed ? item.label : ''}
                    >
                      <span className="nav-icon" data-testid="nav-icon">
                        <Icon size={20} />
                      </span>
                      <span
                        className={`nav-label ${isCollapsed && !isMobile ? 'nav-label-hidden' : ''}`}
                        data-testid="nav-label"
                      >
                        {item.label}
                      </span>
                      {(!isCollapsed || isMobile) && (
                        <ChevronDown
                          size={16}
                          className={`nav-group-chevron ${expanded ? 'open' : ''}`}
                        />
                      )}
                    </button>

                    {/* Inline submenu (always in DOM; CSS toggles visibility) */}
                    <div className={`nav-submenu ${expanded && (!isCollapsed || isMobile) ? 'open' : ''}`}>
                      <div className="nav-submenu-inner">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = location.pathname === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`sidebar-link nav-sublink ${childActive ? 'active' : ''}`}
                              onClick={() => { if (isMobile && onClose) onClose(); }}
                              data-testid={`nav-item-${child.id}`}
                            >
                              <span className="nav-icon"><ChildIcon size={18} /></span>
                              <span className="nav-label">{child.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>

                    {/* Collapsed flyout submenu on hover */}
                    {isCollapsed && hoveredItem === item.id && (
                      <div className="sidebar-tooltip sidebar-flyout">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`sidebar-flyout-link ${location.pathname === child.path ? 'active' : ''}`}
                            onClick={() => { if (isMobile && onClose) onClose(); }}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = item.path === '/'
                ? location.pathname === '/' || location.pathname === '/dashboard'
                : location.pathname === item.path;

              return (
                <div
                  key={item.path}
                  className="nav-item-wrapper"
                  onMouseEnter={() => { if (isCollapsed) setHoveredItem(item.id); }}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <Link
                    to={item.path}
                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                    onClick={() => { if (isMobile && onClose) onClose(); }}
                    data-testid={`nav-item-${item.id}`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <span className="nav-icon" data-testid="nav-icon">
                      <Icon size={20} />
                    </span>
                    <span
                      className={`nav-label ${isCollapsed && !isMobile ? 'nav-label-hidden' : ''}`}
                      data-testid="nav-label"
                    >
                      {item.label}
                    </span>
                  </Link>

                  {isCollapsed && hoveredItem === item.id && (
                    <div className="sidebar-tooltip">{item.label}</div>
                  )}
                </div>
              );
            })
          )}
        </nav>

        <div className="sidebar-footer">
          {(!isCollapsed || isMobile) && (
            <p className="sidebar-brand">
              {user?.role === 'super_admin' ? `${textConfig.appName} Super Admin` :
               (user?.role === 'admin' || user?.is_admin) ? `${textConfig.appName} Admin` :
               textConfig.appName}
            </p>
          )}
        </div>
      </aside>
    </>
  );
};

export default CollapsibleSidebar;
